import { useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { approveDiary, getDiary, submitDiary } from "../../../services/WeeklyDiaryService";
import { handleApiError } from "../../../utils/errorHandler";
import { getSuccessMessage, toApiDate } from "../../LessonPlanner/utils/lessonPlanUtils";
import { SEARCH_DEBOUNCE_MS } from "../WeeklyDiaryConstant";
import { normalizeDiary } from "../utils/diaryUtils";

/** One teacher's diary for a subject and week: load; teacher submits; reviewer approves. */
export const useTeacherDiary = ({ teacherId, subjectId, weekStart }) => {
  const [diary, setDiary] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestRef = useRef(0);
  const weekParam = toApiDate(weekStart);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  /* ================= FETCH ================= */
  const fetchDiary = useCallback(async () => {
    const requestId = ++requestRef.current;
    setIsLoading(true);
    try {
      const response = await getDiary({
        teacher_id: teacherId,
        subject_id: subjectId,
        week_start: weekParam,
        status,
        search: debouncedSearch,
      });
      if (requestId === requestRef.current) setDiary(normalizeDiary(response));
    } catch (error) {
      if (requestId === requestRef.current) handleApiError(error);
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, [teacherId, subjectId, weekParam, status, debouncedSearch]);

  useEffect(() => {
    fetchDiary();
  }, [fetchDiary]);

  /* ================= SUBMIT (teacher) ================= */
  const submit = useCallback(
    async (reflection) => {
      setIsSubmitting(true);
      try {
        const response = await submitDiary({
          subject_id: Number(subjectId),
          week_start: weekParam,
          reflection: reflection.trim(),
        });
        enqueueSnackbar(getSuccessMessage(response, "Weekly diary submitted"), { variant: "success" });
        await fetchDiary();
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [subjectId, weekParam, fetchDiary],
  );

  /* ================= APPROVE (reviewer) ================= */
  const approve = useCallback(
    async (remarks) => {
      setIsApproving(true);
      try {
        const response = await approveDiary({
          teacher_id: Number(teacherId),
          subject_id: Number(subjectId),
          week_start: weekParam,
          remarks: remarks.trim(),
        });
        enqueueSnackbar(getSuccessMessage(response, "Diary approved"), { variant: "success" });
        await fetchDiary();
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setIsApproving(false);
      }
    },
    [teacherId, subjectId, weekParam, fetchDiary],
  );

  return {
    diary,
    status,
    setStatus,
    search,
    setSearch,
    isLoading,
    isApproving,
    isSubmitting,
    approve,
    submit,
  };
};
